import { toast } from 'react-toastify'
import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects'
import apis from '../apis'
import { ReturnCall } from '../types'
import { selectFilters } from './StockMovementsSeletor'
import { actions } from './StockMovementsState'
import * as api from './StockMovementsApi'
import { PayloadAction } from '@reduxjs/toolkit'
import {
  BatchItem, ValidatePkModelBarcodeSagaParams, GetAddressSagaParams, GetBatchInfoSagaParams,
  GetBatchParams, GetPkItemsSagaParams, MoveBatchSagaParams, MovementErrorStatus, PostMovimentationSagaParams,
  StockMovementsState, ValidateSupervisorBadgeParams, PkItem, PkItemMovementSagaParams, PkBatch, PkItemDidNotFitsSagaParams,
} from './StockMovementsTypes'
import { actions as appActions } from '../state'
import { UnpostedBatchToastBody } from './components/UnpostedBatchToastBody'
import { formatDateUTC } from 'src/utils/formatDate'

const GET_BATCH_ERROR = 'Falha ao buscar lotes'
const MOVE_BATCH_ERROR = 'Falha ao realizar reposição'
const GET_ADDRESS_ERROR = 'Falha ao buscar endereço'
const GET_BATCH_INFO_ERROR = 'Falha ao buscar informações do lote'
const POST_MOVIMENTATION_ERROR = 'Falha ao movimentar lotes'

export function* getMovement() {
  yield put(appActions.isLoading(true))
  try {
    const {
      page, fulfillmentCenterId, startDate, endDate, modelId, type, description, sourceSystem, code, user,
    } = yield select(selectFilters)
    const { data: movements, totalItems } = yield call(
      apis.getStockMovements,
      {
        page,
        pageSize: 100,
        fulfillmentCenterId,
        type,
        modelId,
        startDate,
        description,
        sourceSystem,
        endDate,
        code,
        user,
      },
    )
    yield put(actions.addMovements(movements))
    yield put(actions.setTotalItems(totalItems))
  } catch (error: any) {
    toast.error(`Ocorreu um erro: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validateSupervisorBadge({ payload }: PayloadAction<ValidateSupervisorBadgeParams>) {
  const { badge, setError, onSuccess } = payload

  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof api.validateSupervisorBadge> = yield call(
      api.validateSupervisorBadge,
      badge,
    )

    if (!response.isOk) {
      setError('Crachá inválido')
      return
    }

    yield put(actions.setBadge(badge))
    onSuccess()
  } catch (error: any) {
    setError('Falha ao validar o crachá do supervisor!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getBatch({ payload }: PayloadAction<GetBatchParams>) {
  const {
    address, batchOrProductCode, badge, setError, onSuccess,
  } = payload

  try {
    yield put(actions.setBatchAndStockBatchDueList(undefined))
    yield put(actions.setBatchAndStockBatchDueSelected(undefined))

    const response: ReturnCall<typeof api.getBatch> = yield call(
      api.getBatch,
      address,
      batchOrProductCode,
      badge,
    )

    const result = response.data

    if (!response.isOk) {
      setError(result.data.message || GET_BATCH_ERROR)
      return
    }

    if (result.length === 0) {
      setError('Nenhum lote encontrado nesse endereço')
      return
    }

    yield put(actions.setBatchAndStockBatchDueList(result))
    if (result.length === 1) yield put(actions.setBatchAndStockBatchDueSelected(result[0]))
    onSuccess()
  } catch (error: any) {
    setError(GET_BATCH_ERROR)
  }
}

export function* moveBatch({ payload }: PayloadAction<MoveBatchSagaParams>) {
  const {
    addressId, lotStockId, qty, userBadge, onSuccess, setDisabledConfirmButton,
  } = payload

  try {
    const response: ReturnCall<typeof api.moveBatch> = yield call(
      api.moveBatch,
      {
        addressId, lotStockId, qty, userBadge,
      },
    )

    if (!response.isOk) {
      toast.error(response.data.data.message || MOVE_BATCH_ERROR)
      setDisabledConfirmButton(false)
      return
    }

    onSuccess()
    toast.success('Lote movimentado com sucesso!')
  } catch (error: any) {
    toast.error(MOVE_BATCH_ERROR)
  }
}

export function* getAddress({ payload }: PayloadAction<GetAddressSagaParams>) {
  const {
    fcId, address, onSuccess, setError,
  } = payload

  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof api.getAddress> = yield call(
      api.getAddress,
      { fcId, address },
    )

    const result = response.data

    if (!response.isOk || result.idLocalizacao === null) {
      setError(result.message || result.data.message || GET_ADDRESS_ERROR)
      return
    }

    const currentPath = window.location.pathname
    const pathPageMovementBatches = '/movement/batches'

    if (currentPath.includes(pathPageMovementBatches)) {
      onSuccess(undefined)
      const checkStockAtAddress: ReturnCall<typeof api.checkStockAtAddress> = yield call(
        api.checkStockAtAddress,
        { address },
      )

      const validade = checkStockAtAddress.data

      if (!checkStockAtAddress.isOk) {
        onSuccess(address)
        setError(validade.message || 'Endereço de destino ainda possui lotes não mapeados, chame o fiscal')
        return
      }
    }
    onSuccess(address)
    yield put(actions.setInputAddress(result))
  } catch (error: any) {
    setError(GET_ADDRESS_ERROR)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getBatchInfo({ payload }: PayloadAction<GetBatchInfoSagaParams>) {
  const {
    batchId, fcId, badge, onSuccess, setError,
  } = payload

  try {
    const response: ReturnCall<typeof api.getBatchInfo> = yield call(
      api.getBatchInfo,
      { batchId, fcId, badge },
    )

    const result = response.data

    if (!response.isOk) {
      setError(result.data.message || GET_BATCH_INFO_ERROR)
      return
    }

    if (result.addresses.length === 1) {
      const address = result.addresses[0]
      const state = yield select()
      const { batchesList }: StockMovementsState = state.stockMovements

      const batchItem: BatchItem = {
        batchId: result.idBatch,
        validity: formatDateUTC(address.dueDate, 'DD/MM/YYYY'),
        quantity: address.quantity,
        address: address.addressFormated,
        idStockBatchDue: address.idStockBatchDue,
        productDescription: result.productDescription,
      }

      const batchItemWasAdded = batchesList.some((batch) => batch.idStockBatchDue === batchItem.idStockBatchDue)
      if (batchItemWasAdded) {
        setError('Esse estoque do lote já foi adicionado!')
        return
      }

      yield put(actions.setBatchesList([...batchesList, batchItem]))

      return
    }

    yield put(actions.setBatchInfo(result))
    onSuccess()
  } catch (error: any) {
    setError(GET_BATCH_INFO_ERROR)
  }
}

export function* postMovimentation({ payload }: PayloadAction<PostMovimentationSagaParams>) {
  const {
    idAddress, movimentations, onSuccess, badge, onError,
  } = payload

  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof api.postMovimentation> = yield call(
      api.postMovimentation,
      { idAddress, movimentations, badge },
    )

    const result = response.data

    if (response.isOk) {
      const failedBatchesStock = result.status.error

      if (failedBatchesStock.length) {
        toast.warn('Falha ao realizar a movimentação dos seguintes lotes:', { autoClose: false })

        const state = yield select()
        const { batchesList }: StockMovementsState = state.stockMovements

        failedBatchesStock.map((failedBatchStock: MovementErrorStatus) => {
          const failedBatchInfo = batchesList.find((batchInfo) => failedBatchStock.id === batchInfo.idStockBatchDue)

          if (!failedBatchInfo) return ''

          return toast.warn(UnpostedBatchToastBody({
            reason: failedBatchStock.message,
            productDescription: failedBatchInfo.productDescription,
            validity: failedBatchInfo.validity,
            quantity: failedBatchInfo.quantity,
            batchId: failedBatchInfo.batchId,
          }), { autoClose: false })
        })

        onError()

        return
      }

      toast.success('Movimentação de lotes concluída com sucesso!')
      onSuccess()

      return
    }

    if (!response.isOk) {
      toast.error(result.data.message || POST_MOVIMENTATION_ERROR)
      return
    }
  } catch (error: any) {
    toast.error(POST_MOVIMENTATION_ERROR)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

const customSort = (a: PkBatch, b: PkBatch): number => {
  const dateComparison = a.expirationDate.localeCompare(b.expirationDate)
  if (dateComparison !== 0) {
    return dateComparison
  }

  if (a.quantity !== b.quantity) {
    return a.quantity - b.quantity
  }

  return Number(a.batchNumber) - Number(b.batchNumber)
}

export function* getPkItems({ payload }: PayloadAction<GetPkItemsSagaParams>) {
  const {
    fcId, address, badge, onSuccess, setError,
  } = payload

  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof api.getPkItems> = yield call(
      api.getPkItems,
      { fcId, address, badge },
    )

    const result = response.data

    if (!response.isOk) {
      setError(result.message || result.data.message)
      return
    }

    const pkItems: PkItem[] = result

    pkItems.forEach((item) => {
      item.batches.forEach((batch) => {
        batch.batchNumber = batch.batchNumber || 0
      })
      item.batches.sort(customSort)
    })

    yield put(actions.setPkItems(pkItems))
    onSuccess()
  } catch (error: any) {
    setError(GET_ADDRESS_ERROR)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validatePkModelBarcode({ payload }: PayloadAction<ValidatePkModelBarcodeSagaParams>) {
  const {
    barcode, item, onSuccess, setError,
  } = payload

  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof api.validatePkModelBarcode> = yield call(
      api.validatePkModelBarcode,
      { barcode, item },
    )

    if (!response.isOk) {
      setError('Produto incorreto')
      return
    }

    onSuccess()
  } catch (error: any) {
    setError('Falha ao validar produto!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* pkItemMovement({ payload }: PayloadAction<PkItemMovementSagaParams>) {
  const {
    item, startedAt, badge, onSuccess,
  } = payload

  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof api.pkItemMovement> = yield call(
      api.pkItemMovement,
      { item, startedAt, badge },
    )

    if (!response.isOk) {
      yield put(actions.updateMovementsField({ key: 'pkModalDisableButtons', value: false }))
      toast.error(response.data.message || response.data.data.message || 'Falha ao movimentar item!')
      return
    }

    if (response.data.status.error.length > 0) {
      if (response.data.status.error.length === item.batches.length) {
        yield put(actions.updateMovementsField({ key: 'pkModalDisableButtons', value: false }))
        toast.error(response.data.status.error[0].message || 'Falha ao movimentar item!')
        return
      }
      const error = response.data.status.error[0].message
      toast.warn(`Alguns lotes foram movimentados, outros não.${error ? ` Motivo: ${error}` : ''}`)
    }

    onSuccess()
  } catch (error: any) {
    yield put(actions.updateMovementsField({ key: 'pkModalDisableButtons', value: false }))
    toast.error('Falha interna!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* pkItemDidNotFits({ payload }: PayloadAction<PkItemDidNotFitsSagaParams>) {
  const {
    items, item, startedAt, badge, onSuccess,
  } = payload

  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof api.pkItemDidNotFits> = yield call(
      api.pkItemDidNotFits,
      {
        items, item, startedAt, badge,
      },
    )

    const result = response.data

    if (!response.isOk) {
      yield put(actions.updateMovementsField({ key: 'pkModalDisableButtons', value: false }))
      toast.error(result.message || result.data.message || 'Falha ao buscar nova posição!')
      return
    }

    const pkItems: PkItem[] = result

    pkItems.map((_item) => (_item.batches.sort(customSort)))

    yield put(actions.setPkItems(pkItems))
    onSuccess()
  } catch (error: any) {
    yield put(actions.updateMovementsField({ key: 'pkModalDisableButtons', value: false }))
    toast.error('Falha interna.')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getMovements.type, getMovement),
    takeLatest(actions.validateSupervisorBadge.type, validateSupervisorBadge),
    takeLatest(actions.getBatch.type, getBatch),
    takeLatest(actions.moveBatch.type, moveBatch),
    takeLatest(actions.getAddress.type, getAddress),
    takeLatest(actions.getBatchInfo.type, getBatchInfo),
    takeLatest(actions.postMovimentation.type, postMovimentation),
    takeLatest(actions.getPkItems.type, getPkItems),
    takeLatest(actions.validatePkModelBarcode.type, validatePkModelBarcode),
    takeLatest(actions.pkItemMovement.type, pkItemMovement),
    takeLatest(actions.pkItemDidNotFits.type, pkItemDidNotFits),
  ])
}
